import { CSSProperties, useMemo } from 'react';
import { POWERED_BY_FOOTER_HEIGHT_PX } from 'config/constants';
import { NavShellState } from 'hooks/nav/useNavShellState';

interface Args {
    navShell: NavShellState;
    useNavbarV2: boolean;
    useGlobalFilterV2: boolean;
    isGlobalFilterCollapsable: boolean;
    isGlobalFilterCollapsed: boolean;
    expandFilterBol: boolean;
    removeGlobalFilter: boolean;
    isAskAsper: boolean;
    isIframeRoute: boolean;
    /** Routes that paint their own edge-to-edge background and own their internal padding. */
    isFullBleedRoute: boolean;
}

export interface ShellLayout {
    /** Fixed inset for the applied-filters ribbon, pinned under whichever navbar is rendered. */
    ribbonInset: CSSProperties;
    mainContainerStyle: CSSProperties;
    mainContainerClassName: string;
}

/**
 * Positions the app shell around `#main-container`. Under V1 this reproduces the utility-class
 * layout exactly; under V2 the content offset varies with rail and panel state, so margin-left
 * becomes an inline value and this is its single owner — FilterV2 opts out of writing it.
 */
export const useShellLayout = ({
    navShell,
    useNavbarV2,
    useGlobalFilterV2,
    isGlobalFilterCollapsable,
    isGlobalFilterCollapsed,
    expandFilterBol,
    removeGlobalFilter,
    isAskAsper,
    isIframeRoute,
    isFullBleedRoute,
}: Args): ShellLayout => {
    const navbarHeightVar = useNavbarV2 ? 'var(--navbar-v2-height)' : 'var(--navbar-height)';

    // Pin ribbon under navbar; left offset follows open sidebar width or full width when filter is collapsed
    const ribbonInset = useMemo<CSSProperties>(() => {
        const right = '1rem';
        const top = navbarHeightVar;
        const zIndex = expandFilterBol ? 53 : 40;
        const transition = '500ms';
        // V2 keeps the ribbon clear of the rail and of whichever panel holds the shared slot
        if (useNavbarV2) return { top, right, left: navShell.shellLeftOffset, zIndex, transition };
        if (removeGlobalFilter && !isGlobalFilterCollapsable) return { top, right, left: '1rem', zIndex, transition };
        if (isGlobalFilterCollapsed) return { top, right, left: '0', zIndex, transition };
        return {
            top,
            right,
            left: useGlobalFilterV2 ? 'var(--global-filter-v2-width)' : 'var(--global-filter-width)',
            zIndex,
            transition,
        };
    }, [
        navbarHeightVar,
        expandFilterBol,
        useNavbarV2,
        navShell.shellLeftOffset,
        removeGlobalFilter,
        isGlobalFilterCollapsable,
        isGlobalFilterCollapsed,
        useGlobalFilterV2,
    ]);

    const mainContainerStyle = useMemo<CSSProperties>(() => {
        if (isAskAsper) {
            return {
                // Reserve sticky PoweredByFooter; agentic layouts fill this shell at 100%
                // (their footerHeight prop uses 100vh and cannot nest under the navbar).
                height: `calc(100vh - ${navbarHeightVar} - ${POWERED_BY_FOOTER_HEIGHT_PX}px)`,
                marginTop: navbarHeightVar,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                ...(useNavbarV2 ? { marginLeft: navShell.shellLeftOffset } : {}),
            };
        }
        return {
            minHeight: 'calc(100vh - 80px)',
            paddingBottom: POWERED_BY_FOOTER_HEIGHT_PX,
            transition: '500ms ease',
            ...(useNavbarV2
                ? {
                      // A full-bleed route runs its background to the shell edge; every other V2
                      // route keeps a 16px gutter between the rail and its content.
                      marginLeft: isFullBleedRoute
                          ? navShell.contentMarginLeft
                          : `calc(${navShell.contentMarginLeft} + 16px)`,
                  }
                : {}),
            ...(isIframeRoute ? { display: 'flex', flexDirection: 'column' as const } : {}),
        };
    }, [
        isAskAsper,
        isIframeRoute,
        isFullBleedRoute,
        navbarHeightVar,
        useNavbarV2,
        navShell.shellLeftOffset,
        navShell.contentMarginLeft,
    ]);

    const mainContainerClassName = useMemo(() => {
        if (isAskAsper) return '';
        // The no-margin-top variant pairs with a rendered ribbon, which brings its own spacer
        const hasRibbon = isGlobalFilterCollapsable && !removeGlobalFilter;
        if (useNavbarV2) {
            // A full-bleed route needs nothing but the navbar offset; it owns its own padding.
            if (isFullBleedRoute) return 'main-screen-v2-margin-top-no-margin-top';
            return `mr-4 mb-4 ${hasRibbon ? 'main-screen-v2-margin-top-no-margin-top' : 'main-screen-v2-margin-top'}`;
        }
        if (removeGlobalFilter) return 'm-4 main-screen-margin-top';
        return `mt-4 mr-4 mb-4 ${useGlobalFilterV2 ? 'global-filter-v2-margin-left' : 'global-filter-margin-left'} ${
            isGlobalFilterCollapsable ? 'main-screen-margin-top-no-margin-top' : 'main-screen-margin-top'
        }`;
    }, [isAskAsper, useNavbarV2, isFullBleedRoute, removeGlobalFilter, useGlobalFilterV2, isGlobalFilterCollapsable]);

    return { ribbonInset, mainContainerStyle, mainContainerClassName };
};

export default useShellLayout;
